import { ReactNode } from "react";
import { ButtonProps, TooltipTrigger } from "react-aria-components";
import { UnstyledRACButton } from "../../buttons/UnstyledRACButton";
import { TooltipBubble } from "../TooltipBubble";

interface ElementIconButtonProps extends ButtonProps {
  /**
   * The accessible label for the button (used for aria-label).
   */
  label: string;
  /**
   * The content to render inside the button (typically an SVG).
   *
   * **Accessibility:** The button provides the accessible name via `aria-label`.
   * To avoid duplicate announcements, pass the SVG with `aria-hidden` (and avoid `title`/`aria-label` on the SVG).
   */
  children: ReactNode;
  /**
   * Optional text for the tooltip bubble. Only provide this if it adds information beyond `label`.
   * If omitted, the tooltip will show `label` visually but is hidden from screen readers to
   * prevent redundant announcements. Ensure `label` contains all essential information.
   */
  tooltipText?: string;
}

/**
 * Renders a clickable action icon with a tooltip.
 *
 * **When to use:**
 * - Interactive action icons (e.g., Switch player, Remove player)
 * - Overlay buttons that need tooltip explanations
 * - Minimal/unstyled icon buttons
 *
 * **When NOT to use:**
 * - For non-interactive status indicators (use `ElementIcon` instead)
 * - For navigation-based icons (use `ElementIconLink` instead)
 * - When you need styled button variants (use `IconButton` instead)
 *
 * **Accessibility:**
 * - The button provides the accessible name via `aria-label={label}` - this is always announced
 * - Provide `tooltipText` only when it adds information beyond `label` (e.g., extra context or explanation)
 * - If `tooltipText` is omitted, the tooltip shows `label` visually but is hidden from screen readers
 * - Ensure `label` contains all essential information for screen reader users
 * - Pass SVG icons with `aria-hidden` to avoid duplicate announcements
 *
 * @example
 * ```tsx
 * // Good - action icon with tooltip
 * <ElementIconButton label="Switch player" onPress={handleSwitch}>
 *   <SubIcon aria-hidden />
 * </ElementIconButton>
 * ```
 */
const ElementIconButton = (props: ElementIconButtonProps) => {
  const { label, children, tooltipText, ...buttonProps } = props;

  return (
    <TooltipTrigger delay={100}>
      <UnstyledRACButton {...buttonProps} aria-label={label}>
        {children}
      </UnstyledRACButton>
      <TooltipBubble>
        {tooltipText && tooltipText !== label ? (
          tooltipText
        ) : (
          <span aria-hidden="true">{label}</span>
        )}
      </TooltipBubble>
    </TooltipTrigger>
  );
};

export default ElementIconButton;
